import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { uploadToSiteMedia } from "@/lib/uploadAsset";
import { Plus, Trash2, RefreshCw, ChevronDown, ChevronUp, Star } from "lucide-react";

type Testimonial = {
  id: string;
  name: string;
  company: string | null;
  title: string | null;
  rating: number;
  quote: string;
  video_url: string | null;
  avatar_url: string | null;
  featured: boolean;
  published: boolean;
  display_order: number;
};

const blank = (): Testimonial => ({
  id: "", name: "", company: "", title: "", rating: 5, quote: "",
  video_url: "", avatar_url: "", featured: false, published: false, display_order: 0,
});

const inp = { background: "hsl(0 0% 12%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" } as React.CSSProperties;
const fg = { color: "hsl(0 0% 90%)" };
const mu = { color: "hsl(0 0% 55%)" };

const TestimonialsManager = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Testimonial | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("testimonials").select("*").order("display_order").order("created_at", { ascending: false });
    if (error) toast({ title: "Load failed", description: error.message, variant: "destructive" });
    setRows((data || []) as Testimonial[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setDraft(blank()); setOpenId("__new__"); };
  const startEdit = (r: Testimonial) => { setDraft({ ...r }); setOpenId(r.id); };
  const cancel = () => { setDraft(null); setOpenId(null); };

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim() || !draft.quote.trim()) {
      toast({ title: "Name and quote required", variant: "destructive" });
      return;
    }
    const payload = {
      name: draft.name, company: draft.company || null, title: draft.title || null,
      rating: draft.rating, quote: draft.quote,
      video_url: draft.video_url || null, avatar_url: draft.avatar_url || null,
      featured: draft.featured, published: draft.published, display_order: draft.display_order,
    };
    const op = openId === "__new__"
      ? supabase.from("testimonials").insert([payload])
      : supabase.from("testimonials").update(payload).eq("id", draft.id);
    const { error } = await op;
    if (error) { toast({ title: "Save failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Saved" });
    cancel(); load();
  };

  const remove = async (r: Testimonial) => {
    if (!confirm(`Delete "${r.name}"?`)) return;
    await supabase.from("testimonials").delete().eq("id", r.id);
    load();
  };

  const upload = async (file: File, kind: "avatar" | "video") => {
    if (!draft) return;
    setUploading(true);
    try {
      const url = await uploadToSiteMedia(file, "testimonials");
      setDraft({ ...draft, [kind === "avatar" ? "avatar_url" : "video_url"]: url });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={fg}>Testimonials</h2>
          <p className="text-sm" style={mu}>Only published testimonials appear on the public site.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 70%)", background: "transparent" }}>
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={startNew} style={{ background: "hsl(207 68% 50%)", color: "hsl(0 0% 100%)" }}>
            <Plus size={14} className="mr-1" /> New
          </Button>
        </div>
      </div>

      {loading ? <p style={mu}>Loading…</p> : (
        <div className="space-y-2">
          {rows.length === 0 && openId !== "__new__" && <p className="text-center py-8 text-sm" style={mu}>No testimonials yet.</p>}
          {openId === "__new__" && draft && <Editor draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} uploading={uploading} onUpload={upload} isNew />}
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
              <button onClick={() => openId === r.id ? cancel() : startEdit(r)} className="flex w-full items-center justify-between gap-3 p-4 text-left">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate" style={fg}>{r.name}</span>
                    {r.published ? <span className="text-[10px] uppercase rounded px-1.5 py-0.5" style={{ background: "hsl(140 50% 20%)", color: "hsl(140 60% 75%)" }}>Published</span> : <span className="text-[10px] uppercase rounded px-1.5 py-0.5" style={{ background: "hsl(0 0% 14%)", color: "hsl(0 0% 60%)" }}>Draft</span>}
                    {r.featured && <span className="text-[10px] uppercase rounded px-1.5 py-0.5" style={{ background: "hsl(45 80% 18%)", color: "hsl(45 80% 70%)" }}>Featured</span>}
                    <span className="text-xs" style={mu}>{Array.from({ length: r.rating }).map(() => "★").join("")}</span>
                  </div>
                  <div className="mt-1 text-xs truncate" style={mu}>{r.company || ", "} · {r.title || ", "} · "{r.quote.slice(0, 60)}…"</div>
                </div>
                {openId === r.id ? <ChevronUp size={16} style={mu} /> : <ChevronDown size={16} style={mu} />}
              </button>
              {openId === r.id && draft && <Editor draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} onDelete={() => remove(r)} uploading={uploading} onUpload={upload} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function Editor({ draft, setDraft, onSave, onCancel, onDelete, isNew, uploading, onUpload }: {
  draft: Testimonial; setDraft: (d: Testimonial) => void;
  onSave: () => void; onCancel: () => void; onDelete?: () => void; isNew?: boolean;
  uploading: boolean; onUpload: (f: File, kind: "avatar" | "video") => void;
}) {
  return (
    <div className="border-t p-4 space-y-3" style={{ borderColor: "hsl(0 0% 14%)" }}>
      <div className="grid gap-3 md:grid-cols-3">
        <div><Label style={mu}>Name</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} style={inp} /></div>
        <div><Label style={mu}>Company</Label><Input value={draft.company || ""} onChange={(e) => setDraft({ ...draft, company: e.target.value })} style={inp} /></div>
        <div><Label style={mu}>Title</Label><Input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} style={inp} /></div>
      </div>
      <div><Label style={mu}>Quote</Label><Textarea rows={3} value={draft.quote} onChange={(e) => setDraft({ ...draft, quote: e.target.value })} style={inp} /></div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label style={mu}>Avatar</Label>
          <div className="flex items-center gap-3">
            {draft.avatar_url ? <img src={draft.avatar_url} alt="" className="h-12 w-12 rounded-full object-cover" /> : <div className="h-12 w-12 rounded-full" style={{ background: "hsl(0 0% 14%)" }} />}
            <Input type="file" accept="image/*" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f, "avatar"); e.target.value = ""; }} style={inp} />
          </div>
          <Input className="mt-2" placeholder="…or paste image URL" value={draft.avatar_url || ""} onChange={(e) => setDraft({ ...draft, avatar_url: e.target.value })} style={inp} />
        </div>
        <div>
          <Label style={mu}>Video (URL or upload)</Label>
          <Input placeholder="YouTube/Vimeo/MP4 URL" value={draft.video_url || ""} onChange={(e) => setDraft({ ...draft, video_url: e.target.value })} style={inp} />
          <Input type="file" accept="video/*" disabled={uploading} className="mt-2" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f, "video"); e.target.value = ""; }} style={inp} />
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <div>
          <Label style={mu}>Rating</Label>
          <div className="flex gap-1">
            {[1,2,3,4,5].map((n) => (
              <button key={n} onClick={() => setDraft({ ...draft, rating: n })}>
                <Star size={20} fill={n <= draft.rating ? "hsl(45 80% 55%)" : "transparent"} color="hsl(45 80% 55%)" />
              </button>
            ))}
          </div>
        </div>
        <div><Label style={mu}>Display order</Label><Input type="number" value={draft.display_order} onChange={(e) => setDraft({ ...draft, display_order: Number(e.target.value) })} style={inp} /></div>
        <div className="flex items-end gap-4 text-sm" style={fg}>
          <label className="flex items-center gap-2"><input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} /> Published</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={draft.featured} onChange={(e) => setDraft({ ...draft, featured: e.target.checked })} /> Featured</label>
        </div>
      </div>
      {uploading && <p className="text-xs" style={{ color: "hsl(207 68% 60%)" }}>Uploading…</p>}
      <div className="flex justify-end gap-2 pt-2">
        {!isNew && onDelete && <Button size="sm" variant="outline" onClick={onDelete} className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 70% 60%)", background: "transparent" }}><Trash2 size={14} className="mr-1" /> Delete</Button>}
        <Button size="sm" variant="outline" onClick={onCancel} className="border" style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 70%)", background: "transparent" }}>Cancel</Button>
        <Button size="sm" onClick={onSave} style={{ background: "hsl(207 68% 50%)", color: "hsl(0 0% 100%)" }}>Save</Button>
      </div>
    </div>
  );
}

export default TestimonialsManager;
